import React, { ReactNode, ComponentProps, useCallback, useMemo } from 'react';

import {
  useUsers,
  useRoles,
  useCompanies,
  useDeletedUsers,
  useFormattedUser,
} from '@adsk/alloy-provider';
import {
  DropdownMenu,
  DropdownProps,
  GetFilteredItems,
  VirtualProps,
  VirtualizedMenu,
  FooterProps,
} from '@adsk/alloy-react-dropdown';
import EmptyState from '@adsk/alloy-react-empty-state';

import {
  ASSIGNEE_PICKER_COMPANY,
  ASSIGNEE_PICKER_ROLE,
  ASSIGNEE_PICKER_USER,
  DEFAULT_OPTIONS_ORDER,
  DEFAULT_USERS_STATUS_FILTER,
  ASSIGNEE_PICKER_UNASSIGNED,
  AUTODESK_ID_VALUE_KEY,
  MEMBER_GROUP_ID_VALUE_KEY,
  DEFAULT_STATUS_FILTERS,
} from '../consts';
import {
  transformCompanies,
  transformUsers,
  transformRoles,
} from '../helpers/formatters';
import { sortUsersAlphabetically } from '../helpers/utils';
import {
  AssigneeGroup,
  GroupedItem,
  UseAssigneePickerProps,
  ItemWithGroup,
  AssigneePickerFooterProps,
  AssigneePickerRenderMenuProps,
} from '../types';
import { AssigneePickerMenuItem } from './AssigneePickerMenuItem';
import { useAssigneePickerAnalytics } from './useAssigneePickerAnalytics';
import { useTranslation } from '../i18n';

type ReturnType = {
  dropdownProps: Pick<
    DropdownProps<ItemWithGroup>,
    | 'isLoading'
    | 'groupedItems'
    | 'renderMenu'
    | 'isAutocomplete'
    | 'showGroupItemCount'
    | 'allowGroupFiltering'
  > & {
    getFilteredItems: GetFilteredItems<ItemWithGroup>;
    renderNoAutocompleteResults: () => ReactNode;
  };
};

const defaultRenderMenu = ({
  menuProps,
  virtualProps,
  assigneePickerFooterProps,
}: AssigneePickerRenderMenuProps) => {
  return VirtualizedMenu.render(
    {
      ...menuProps,
      renderFooter: (footerProps: FooterProps) =>
        menuProps.renderFooter
          ? menuProps.renderFooter({
              ...footerProps,
              ...assigneePickerFooterProps,
            })
          : null,
    },
    virtualProps,
  );
};

/**
 * A Dropdown component to multiselect items from a dropdown that
 * contains Users, Roles and Companies grouped by category
 */
export const useAssigneePicker = ({
  companyOptionValue = MEMBER_GROUP_ID_VALUE_KEY,
  roleOptionValue = MEMBER_GROUP_ID_VALUE_KEY,
  userOptionValue = AUTODESK_ID_VALUE_KEY,
  companyOptionLabel,
  roleOptionLabel,
  userOptionLabel,
  userOptionSubtextRenderer,
  optionsOrder = DEFAULT_OPTIONS_ORDER,
  statusFilters = DEFAULT_STATUS_FILTERS,
  optionsFilter = {},
  additionalCompanyDataFields,
  additionalUserDataFields,
  additionalRoleDataFields,
  customUsers,
  customRoles,
  customCompanies,
  customUnassigned,
  postFormatters,
  hideDeletedEntitiesInMenu = { users: true },
  hideInactiveEntitiesInMenu = true,
  renderFooter,
  renderMenuItem = AssigneePickerMenuItem,
  renderMenu = defaultRenderMenu,
}: UseAssigneePickerProps | undefined = {}) => {
  const { t } = useTranslation();

  const UNASSIGNED_ITEM = useMemo(
    () =>
      customUnassigned ||
      ({
        label: t('UNASSIGNED'),
        items: [
          {
            label: t('UNASSIGNED'),
            value: 'null',
            group: 'unassigned',
          },
        ],
        group: ASSIGNEE_PICKER_UNASSIGNED,
      } satisfies GroupedItem),
    [customUnassigned, t],
  );

  const alloyUsers = useUsers();
  const users = useMemo(
    () => customUsers || alloyUsers,
    [customUsers, alloyUsers],
  );

  const { users: statusFiltersUsers = DEFAULT_USERS_STATUS_FILTER } =
    statusFilters;

  const includeDeletedUsers = useMemo(
    () => statusFiltersUsers.includes('deleted') && !customUsers,
    [customUsers, statusFiltersUsers],
  );
  const alloyDeletedUsers = useDeletedUsers({ disable: !includeDeletedUsers });

  const alloyRoles = useRoles();
  const roles = useMemo(
    () => customRoles || alloyRoles,
    [customRoles, alloyRoles],
  );

  const alloyCompanies = useCompanies();
  const companies = useMemo(
    () => customCompanies || alloyCompanies,
    [customCompanies, alloyCompanies],
  );

  const isLoading = useMemo(
    () =>
      (includeDeletedUsers
        ? [users, roles, companies, alloyDeletedUsers]
        : [users, roles, companies]
      ).some((entity) => entity?.loading),
    [includeDeletedUsers, users, roles, companies, alloyDeletedUsers],
  );

  const { formatDisplayUser } = useFormattedUser();
  const companyOptionsFilter = useMemo(
    () => optionsFilter[ASSIGNEE_PICKER_COMPANY],
    [optionsFilter],
  );
  const roleOptionsFilter = useMemo(
    () => optionsFilter[ASSIGNEE_PICKER_ROLE],
    [optionsFilter],
  );
  const userOptionsFilter = useMemo(
    () => optionsFilter[ASSIGNEE_PICKER_USER],
    [optionsFilter],
  );

  const groupedItems = useMemo(() => {
    const [companyIdToNameMap, companyOptions] = transformCompanies(
      companies.data,
      companyOptionValue,
      t,
      statusFilters.companies,
      companyOptionsFilter,
      additionalCompanyDataFields,
      postFormatters?.postTransformCompanies,
    );

    const [rolesMemberCount, userOptions] = transformUsers(
      includeDeletedUsers
        ? sortUsersAlphabetically([...users.data, ...alloyDeletedUsers.data])
        : users.data,
      userOptionValue,
      formatDisplayUser,
      t,
      statusFiltersUsers,
      userOptionsFilter,
      additionalUserDataFields,
      companyIdToNameMap,
      roles.data,
      userOptionSubtextRenderer,
      postFormatters?.postTransformUsers,
      includeDeletedUsers && hideDeletedEntitiesInMenu.users,
    );

    const roleItems = transformRoles(
      roles.data,
      roleOptionValue,
      t,
      statusFilters.roles,
      roleOptionsFilter,
      additionalRoleDataFields,
      rolesMemberCount,
      postFormatters?.postTransformRoles,
      hideInactiveEntitiesInMenu,
    );

    const allOptions: { [key in AssigneeGroup]: GroupedItem[] } = {
      unassigned: [UNASSIGNED_ITEM],
      company: [
        {
          label: companyOptionLabel || t('COMPANIES'),
          items: companyOptions,
          group: ASSIGNEE_PICKER_COMPANY,
        },
      ],
      role: [
        {
          label: roleOptionLabel || t('ROLES'),
          items: roleItems,
          group: ASSIGNEE_PICKER_ROLE,
        },
      ],
      user: [
        {
          label: userOptionLabel || t('MEMBERS'),
          items: userOptions,
          group: ASSIGNEE_PICKER_USER,
        },
      ],
    };

    return isLoading
      ? []
      : optionsOrder.reduce<GroupedItem[]>(
          (acc, category) => [...acc, ...allOptions[category]],
          [],
        );
  }, [
    companies.data,
    companyOptionValue,
    t,
    statusFilters.companies,
    statusFilters.roles,
    companyOptionsFilter,
    additionalCompanyDataFields,
    postFormatters?.postTransformCompanies,
    postFormatters?.postTransformUsers,
    postFormatters?.postTransformRoles,
    includeDeletedUsers,
    users.data,
    alloyDeletedUsers.data,
    userOptionValue,
    formatDisplayUser,
    statusFiltersUsers,
    userOptionsFilter,
    additionalUserDataFields,
    userOptionSubtextRenderer,
    hideDeletedEntitiesInMenu.users,
    roles.data,
    roleOptionValue,
    roleOptionsFilter,
    additionalRoleDataFields,
    hideInactiveEntitiesInMenu,
    UNASSIGNED_ITEM,
    companyOptionLabel,
    roleOptionLabel,
    userOptionLabel,
    isLoading,
    optionsOrder,
  ]);

  const { searchTextRef, getSetSelectedGroupKey, renderMenuHandler } =
    useAssigneePickerAnalytics({ groupedItems });

  const getFilteredItems = useCallback(
    ({
      allItems,
      inputValue = '',
    }: {
      allItems: ItemWithGroup[];
      inputValue?: string;
    }) => {
      searchTextRef.current = inputValue;
      const search = inputValue.trim().toLowerCase();

      if (!search) return allItems;

      return allItems.filter((item) => {
        const { label, subtext } = item;

        return (
          (label && label.toString().toLowerCase().indexOf(search) >= 0) ||
          (subtext && subtext.toString().toLowerCase().indexOf(search) >= 0)
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- leaving out searchTextRef as it is a ref
    [],
  );

  const renderNoAutocompleteResults = useCallback(
    () => (
      <EmptyState
        title={t('NO_RESULTS_TITLE')}
        description={t('NO_RESULTS_DESCRIPTION')}
      />
    ),
    [t],
  );

  const renderMenuFunc = useCallback(
    (
      menuProps: ComponentProps<typeof DropdownMenu<ItemWithGroup>>,
      virtualProps: VirtualProps,
    ) => {
      renderMenuHandler(menuProps);

      const assigneePickerFooterProps: AssigneePickerFooterProps = {
        users: users.data,
        userOptionValueGetter: userOptionValue,
      };

      return renderMenu({
        menuProps: {
          isLoading,
          renderFooter,
          renderMenuItem,
          ...menuProps,
          setSelectedGroupKey: getSetSelectedGroupKey(menuProps),
        },
        virtualProps,
        assigneePickerFooterProps,
      });
    },
    [
      renderMenuHandler,
      users.data,
      userOptionValue,
      renderMenu,
      isLoading,
      getSetSelectedGroupKey,
      renderFooter,
      renderMenuItem,
    ],
  );

  return {
    dropdownProps: {
      isLoading,
      groupedItems,
      getFilteredItems,
      renderMenu: renderMenuFunc,
      renderNoAutocompleteResults,
      showGroupItemCount: true,
      isAutocomplete: true,
      allowGroupFiltering: true,
    },
  } satisfies ReturnType;
};
